/** Display labels for form fields (internal keys used in React state). */
export const FORM_FIELD_LABELS: Record<string, string> = {
  projectTitle: "Program Name",
  goal: "Long-term impact",
  aim: "Medium-term impact(s)",
  objectives: "Short-term impact(s)",
  activities: "Program activities/outputs",
  externalInfluences: "Mapping external factors",
};

/** Canonical toc_data keys stored in Supabase (aligned with UI). */
export const TOC_CANONICAL = {
  programName: "programName",
  longTermImpact: "longTermImpact",
  mediumTermImpact: "mediumTermImpact",
  shortTermImpact: "shortTermImpact",
  programActivities: "programActivities",
  mappingExternalFactors: "mappingExternalFactors",
} as const;

/** Admin / API display labels for toc_data keys (canonical + legacy). */
export const TOC_DATA_FIELD_LABELS: Record<string, string> = {
  programName: "Program Name",
  longTermImpact: "Long-term impact",
  mediumTermImpact: "Medium-term impact(s)",
  shortTermImpact: "Short-term impact(s)",
  programActivities: "Program activities/outputs",
  mappingExternalFactors: "Mapping external factors",
  projectTitle: "Program Name",
  bigPictureGoal: "Long-term impact",
  projectAim: "Medium-term impact(s)",
  objectives: "Short-term impact(s)",
  activities: "Program activities/outputs",
  externalFactors: "Mapping external factors",
};

export function getTocDataFieldLabel(key: string): string {
  return TOC_DATA_FIELD_LABELS[key] || key;
}

function pick<T>(...values: (T | undefined | null)[]): T | undefined {
  for (const v of values) {
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

/** Read program name from toc payload (canonical or legacy). */
export function readProgramName(
  toc: Record<string, unknown> | undefined | null,
  fallback = ""
): string {
  if (!toc) return fallback;
  const name = pick(
    toc[TOC_CANONICAL.programName] as string | undefined,
    toc.projectTitle as string | undefined
  );
  return typeof name === "string" ? name : fallback;
}

/**
 * One impact card / form block — never split on commas (fixes accidental multi-card UI).
 * Legacy rows stored as JSON arrays or comma-split strings are merged back into one block.
 */
export function coerceToSingleString(value: unknown): string {
  if (value == null) return "";

  if (Array.isArray(value)) {
    return value
      .map((item) => String(item ?? ""))
      .filter((item) => item.length > 0)
      .join("\n");
  }

  const str = String(value);
  if (!str) return "";

  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => String(item ?? ""))
        .filter((item) => item.length > 0)
        .join("\n");
    }
    if (typeof parsed === "string") {
      return parsed;
    }
  } catch {
    // plain text — keep whole string (do not split on commas or trim spaces)
  }

  if (str.includes("|||")) {
    return str
      .split("|||")
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .join("\n")
      .trim();
  }

  return str;
}

/** Form state JSON/plain value → single-card JSON string for React state. */
export function singleStringToFormField(value: unknown): string {
  if (typeof value === "string") {
    // Textarea input — preserve spaces while the user is typing (do not trim).
    return JSON.stringify([value]);
  }
  const text = coerceToSingleString(value);
  return JSON.stringify([text]);
}

/** External factors only — comma/newline separated list (max 5 in UI). */
export function coerceToExternalFactorList(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => String(item).split(/\n|,/))
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .slice(0, 5);
  }

  const str = String(value).trim();
  if (!str) return [];

  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed
        .flatMap((item) => String(item).split(/\n|,/))
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .slice(0, 5);
    }
  } catch {
    // fall through
  }

  if (str.includes("|||")) {
    return str
      .split("|||")
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .slice(0, 5);
  }

  const parts = str
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  return (parts.length > 0 ? parts : [str]).slice(0, 5);
}

/** Form state → plain string for long/medium-term impact in DB. */
export function formFieldToStorageScalar(formValue: string): string {
  return coerceToSingleString(formValue).trim();
}

/** Form state → single-item array for short-term / activities in DB. */
export function formFieldToStorageArray(formValue: string): string[] {
  const text = coerceToSingleString(formValue).trim();
  return text ? [text] : [];
}

/** DB scalar → form JSON string for one textarea / one visual card. */
export function storageScalarToFormField(value: unknown): string {
  return singleStringToFormField(value);
}

/** DB array/scalar → form JSON string for one textarea / one visual card. */
export function storageArrayToFormField(value: unknown): string {
  return singleStringToFormField(value);
}

/** DB array → comma-separated string for external influences form field. */
export function storageArrayToExternalFormField(value: unknown): string {
  const items = coerceToExternalFactorList(value);
  return items.join(", ");
}

export type TocFormSlice = {
  goal: string;
  aim: string;
  objectives: string;
  activities: string;
  externalInfluences: string;
};

export function buildTocDataPayload(slice: TocFormSlice, programName: string) {
  return {
    [TOC_CANONICAL.programName]: programName,
    [TOC_CANONICAL.longTermImpact]: formFieldToStorageScalar(slice.goal),
    [TOC_CANONICAL.mediumTermImpact]: formFieldToStorageScalar(slice.aim),
    [TOC_CANONICAL.shortTermImpact]: formFieldToStorageArray(slice.objectives),
    [TOC_CANONICAL.programActivities]: formFieldToStorageArray(slice.activities),
    [TOC_CANONICAL.mappingExternalFactors]: formFieldToStorageArray(
      slice.externalInfluences
    ),
  };
}

type ColorSection = { bg: string; text: string };

export function buildTocColorPayload(
  columnColors: {
    goal?: ColorSection;
    aim?: ColorSection;
    objectives?: ColorSection;
    activities?: ColorSection;
  },
  externalFactorColors: ColorSection[]
) {
  return {
    [TOC_CANONICAL.longTermImpact]: columnColors.goal || {
      bg: "#ffffff",
      text: "#111111",
    },
    [TOC_CANONICAL.mediumTermImpact]: columnColors.aim || {
      bg: "#ffffff",
      text: "#111111",
    },
    [TOC_CANONICAL.shortTermImpact]: columnColors.objectives || {
      bg: "#ffffff",
      text: "#111111",
    },
    [TOC_CANONICAL.programActivities]: columnColors.activities || {
      bg: "#ffffff",
      text: "#111111",
    },
    [TOC_CANONICAL.mappingExternalFactors]: externalFactorColors,
  };
}

export const DEFAULT_PUZZLE_COLOR: ColorSection = {
  bg: "#f3a8b0",
  text: "#333333",
};

const DEFAULT_CLOUD_COLOR: ColorSection = DEFAULT_PUZZLE_COLOR;

const LEGACY_DEFAULT_PUZZLE_BACKGROUNDS = new Set([
  "#cbe3ff",
  "#dbeafe",
  "#bfdbfe",
]);

function normalizePuzzleColorSection(color: ColorSection): ColorSection {
  const bg = String(color.bg || "").trim().toLowerCase();

  // Keep user-customized colors. Only remap known legacy default blues to pink.
  if (LEGACY_DEFAULT_PUZZLE_BACKGROUNDS.has(bg)) {
    return { ...DEFAULT_PUZZLE_COLOR };
  }

  return color;
}

/** Expand or trim puzzle colors; new slots inherit the first entry (shared customise colour). */
export function syncPuzzleColorsToCount(
  colors: ColorSection[],
  count: number
): ColorSection[] {
  if (count <= 0) return [];
  const shared = colors[0] ?? DEFAULT_PUZZLE_COLOR;
  return Array.from({ length: count }, (_, idx) => colors[idx] ?? shared);
}

/** Parse external-influence labels from the form field (JSON array or comma-separated). */
export function parseExternalInfluencesFromForm(value: string): string[] {
  if (!value || !value.trim()) return [];

  const trimmed = value.trim();
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed
        .filter(
          (item) => item !== null && item !== undefined && String(item).trim() !== ""
        )
        .map((item) => String(item).trim());
    }
  } catch {
    // fall back to comma-separated
  }

  return trimmed
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s);
}

/** Colours to persist for puzzles (autosave / publish). Never returns an empty array. */
export function buildPuzzleColorsForSave(
  cloudColors: ColorSection[],
  externalInfluences: string
): ColorSection[] {
  const activeCount = parseExternalInfluencesFromForm(externalInfluences).length;
  const base =
    cloudColors.length > 0 ? cloudColors : [{ ...DEFAULT_PUZZLE_COLOR }];

  if (activeCount > 0) {
    return syncPuzzleColorsToCount(base, activeCount);
  }

  return base;
}

function isColorSection(value: unknown): value is ColorSection {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as ColorSection).bg === "string" &&
    typeof (value as ColorSection).text === "string"
  );
}

/** Parse external-factor colors from array, indexed object, or single section. */
export function parseExternalCloudColors(externalColors: unknown): ColorSection[] {
  if (!externalColors) return [DEFAULT_CLOUD_COLOR];

  if (Array.isArray(externalColors)) {
    const parsed = externalColors
      .filter(isColorSection)
      .map(normalizePuzzleColorSection);
    return parsed.length > 0 ? parsed : [DEFAULT_CLOUD_COLOR];
  }

  if (typeof externalColors === "object") {
    const obj = externalColors as Record<string, unknown>;

    if (isColorSection(externalColors)) {
      return [normalizePuzzleColorSection(externalColors)];
    }

    const indexed: ColorSection[] = [];
    for (let i = 0; i < 10; i++) {
      const entry = obj[i.toString()];
      if (isColorSection(entry)) {
        indexed.push(normalizePuzzleColorSection(entry));
      } else {
        break;
      }
    }
    return indexed.length > 0 ? indexed : [DEFAULT_CLOUD_COLOR];
  }

  return [DEFAULT_CLOUD_COLOR];
}

/** Resolve toc_color section for a form field (canonical or legacy key). */
export function readTocColorSection(
  colors: Record<string, unknown> | undefined | null,
  canonicalKey: string,
  legacyKey: string
): ColorSection | undefined {
  if (!colors) return undefined;
  const section = pick(
    colors[canonicalKey] as ColorSection | undefined,
    colors[legacyKey] as ColorSection | undefined
  );
  if (section && typeof section === "object" && "bg" in section) {
    return section as ColorSection;
  }
  return undefined;
}
